/**
 * screens/settings/hq.tsx — HQ Console (CR-MTR-CON-001, CONTRACT R13).
 * An HQ sees every outlet's balances, funds them (Billing POST /v1/transfers),
 * shares its numbers with outlets (Platform cross-org binding) and caps each
 * outlet's HQ-paid sends. Every amount is a Billing display string; nothing here
 * computes money. Reads only — no local copy of transfers or bindings.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { View, Text, ScrollView, ActivityIndicator } from 'react-native';

import { useTheme } from '../../context/themeContext';
import { useSession } from '../../context/sessionContext';
import { useToast } from '../../components/toast';
import { SettingsHeader, Panel, Tag, DataTable, Divider } from '../../components/platform/primitives';
import { PButton, PModal, PSelect, PTextInput, PEmpty } from '../../components/platform/controls';
import Alert from '../../components/alert';
import { FS, FW } from '../../utils/platformTokens';
import { getApiErrorMessage } from '../../utils/errorHandler';
import { productDisplayName } from '../../navigation/planGate';
import {
  getHqOverview, getHqActivity, createTransfer, listConnections, bindConnection, unbindConnection, setBindingCap,
} from '../../services/platform/platformApi';
import type { HqOverview, HqActivity, Connection, ConnectionBinding, TransferRequest } from '../../schema/platform/types';
import { CAPABILITY_LABEL } from './connections';

type Outlet = HqOverview['outlets'][number];

const expiryNote = (iso: string | null) =>
  iso
    ? `Sent credits keep their expiry: ${new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}.`
    : "These credits don't expire.";

const TransferModal: React.FC<{
  outlet: Outlet | null;
  kind: 'units' | 'money';
  hq: HqOverview['hq'];
  onClose: () => void;
  onDone: () => void;
}> = ({ outlet, kind: initialKind, hq, onClose, onDone }) => {
  const { theme } = useTheme();
  const c = theme.colors;
  const toast = useToast();
  const [kind, setKind] = useState(initialKind);
  const [item, setItem] = useState(hq.units[0]?.item ?? '');
  const [qty, setQty] = useState('');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!outlet) return;
    setKind(initialKind); setItem(hq.units[0]?.item ?? ''); setQty(''); setAmount(''); setNote(''); setErr(null);
  }, [outlet, initialKind, hq.units]);

  if (!outlet) return null;
  const currencyMismatch = outlet.currency !== hq.currency;
  const source = hq.units.find((u) => u.item === item);

  const submit = async () => {
    setErr(null);
    const base = { fromOrgId: hq.orgId, toOrgId: outlet.orgId, note: note.trim() || undefined };
    let body: TransferRequest;
    if (kind === 'units') {
      const quantity = Number(qty);
      if (!item || !Number.isInteger(quantity) || quantity <= 0) { setErr('Enter a whole number of credits.'); return; }
      body = { ...base, kind, item, quantity };
    } else {
      const amountMinor = Math.round(Number(amount) * hq.minorPerUnit);
      if (!Number.isFinite(amountMinor) || amountMinor <= 0) { setErr('Enter an amount to send.'); return; }
      body = { ...base, kind, amountMinor };
    }
    setSaving(true);
    try {
      await createTransfer(body);
      toast.success(`Sent to ${outlet.name}.`);
      onDone();
      onClose();
    } catch (e) {
      setErr(getApiErrorMessage(e, "Couldn't send the transfer."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <PModal open onClose={onClose} title={`Send to ${outlet.name}`}
      footer={<>
        <PButton kind="ghost" onPress={onClose}>Cancel</PButton>
        <PButton kind="accent" loading={saving} disabled={kind === 'money' && currencyMismatch} onPress={submit}>
          {kind === 'units' ? 'Send credits' : 'Send money'}
        </PButton>
      </>}>
      <View style={{ gap: 14 }}>
        <View style={{ flexDirection: 'row', gap: 8 }}>
          <PButton kind={kind === 'units' ? 'accent' : 'default'} onPress={() => setKind('units')}>Credits</PButton>
          <PButton kind={kind === 'money' ? 'accent' : 'default'} onPress={() => setKind('money')}>Money</PButton>
        </View>
        {kind === 'units' ? (
          <>
            <PSelect label="What to send" value={item} onChange={setItem}
              options={hq.units.map((u) => ({ value: u.item, label: `${u.label} · ${u.display} available` }))} />
            <PTextInput label="Quantity" mono keyboardType="numeric" value={qty} onChangeText={(v) => setQty(v.replace(/[^0-9]/g, ''))} />
            {source && <Alert tone="info">{expiryNote(source.expiresAt)}</Alert>}
          </>
        ) : currencyMismatch ? (
          <Alert tone="warn" title="Money can't be sent to this outlet">
            Money moves only between organisations billed in the same currency. HQ is billed in {hq.currency}; {outlet.name} in {outlet.currency}. You can still send credits.
          </Alert>
        ) : (
          <>
            <Text style={{ color: c.ink2, fontSize: FS.md }}>HQ wallet: <Text style={{ fontFamily: theme.font.mono }}>{hq.wallet.display}</Text></Text>
            <PTextInput label={`Amount (${hq.symbol})`} mono keyboardType="numeric" value={amount}
              onChangeText={(v) => setAmount(v.replace(/[^0-9.]/g, ''))} />
            {outlet.relationship === 'franchise' && (
              <Alert tone="info">A tax invoice may apply to money sent to a franchise outlet.</Alert>
            )}
          </>
        )}
        <PTextInput label="Note (optional)" value={note} onChangeText={setNote} maxLength={200} />
        {!!err && <Alert tone="danger">{err}</Alert>}
      </View>
    </PModal>
  );
};

const CapEditor: React.FC<{ conn: Connection; binding: ConnectionBinding; canEdit: boolean; onSaved: () => void }> = ({ conn, binding, canEdit, onSaved }) => {
  const { theme } = useTheme();
  const toast = useToast();
  const [cap, setCap] = useState(binding.monthlyCap == null ? '' : String(binding.monthlyCap));
  const save = async () => {
    try {
      await setBindingCap(conn.id, binding.id, cap === '' ? null : Number(cap));
      toast.success(`Cap saved for ${binding.orgName}.`);
      onSaved();
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Couldn't save the cap."));
    }
  };
  return (
    <View style={{ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
      <Text style={{ color: theme.colors.ink, fontSize: FS.md, minWidth: 140 }}>{binding.orgName}</Text>
      <Tag tone="neutral">{productDisplayName(binding.productCode)}</Tag>
      {binding.capUsage && <Text style={{ color: theme.colors.ink3, fontSize: FS.sm, fontFamily: theme.font.mono }}>{binding.capUsage}</Text>}
      {canEdit && (
        <>
          <PTextInput mono keyboardType="numeric" placeholder="No cap" value={cap} onChangeText={(v) => setCap(v.replace(/[^0-9]/g, ''))} style={{ width: 110 }} />
          <PButton size="sm" onPress={save}>Save cap</PButton>
          <PButton size="sm" kind="ghost" onPress={() => unbindConnection(conn.id, binding.id).then(onSaved, (e) => toast.error(getApiErrorMessage(e, "Couldn't stop sharing.")))}>Stop sharing</PButton>
        </>
      )}
    </View>
  );
};

const SharedNumbers: React.FC<{ orgId: string; outlets: Outlet[]; canEdit: boolean }> = ({ orgId, outlets, canEdit }) => {
  const { theme } = useTheme();
  const c = theme.colors;
  const toast = useToast();
  const [conns, setConns] = useState<Connection[] | null>(null);
  const [pick, setPick] = useState<Record<string, { outlet: string; product: string }>>({});

  const load = useCallback(() => {
    listConnections(orgId).then(
      (rows) => setConns(rows.filter((x) => x.ownerOrgId === orgId && x.status === 'active')),
      () => setConns([]),
    );
  }, [orgId]);
  useEffect(load, [load]);

  if (conns === null) return <ActivityIndicator color={c.accent} />;
  if (conns.length === 0) return <Text style={{ color: c.ink3 }}>HQ has no connections to share yet. Add one under Connections.</Text>;

  return (
    <View style={{ gap: 4 }}>
      <Text style={{ color: c.ink3, fontSize: FS.sm }}>HQ is alerted at 80% and 100% of an outlet's cap. Sends continue and are charged to HQ.</Text>
      {conns.map((conn, i) => {
        const products = Array.from(new Set(conn.bindings.filter((b) => b.orgId === orgId).map((b) => b.productCode)));
        const shared = conn.bindings.filter((b) => b.orgId !== orgId);
        const sel = pick[conn.id] ?? { outlet: '', product: products[0] ?? '' };
        const share = async () => {
          try {
            await bindConnection(conn.id, sel.outlet, sel.product);
            toast.success('Shared with the outlet.');
            load();
          } catch (e) {
            toast.error(getApiErrorMessage(e, "Couldn't share the number."));
          }
        };
        return (
          <View key={conn.id} style={{ gap: 8, paddingVertical: 10 }}>
            {i > 0 && <Divider style={{ marginVertical: 0 }} />}
            <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <Text style={{ fontWeight: FW.semibold, color: c.ink }}>{conn.displayName}</Text>
              <Tag tone="neutral">{CAPABILITY_LABEL[conn.capability]}</Tag>
            </View>
            {shared.length === 0 ? (
              <Text style={{ color: c.ink3, fontSize: FS.sm }}>No outlets use this yet.</Text>
            ) : shared.map((b) => <CapEditor key={b.id} conn={conn} binding={b} canEdit={canEdit} onSaved={load} />)}
            {canEdit && products.length > 0 && (
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                <PSelect size="sm" width={200} value={sel.outlet}
                  onChange={(v) => setPick((p) => ({ ...p, [conn.id]: { ...sel, outlet: v } }))}
                  options={[{ value: '', label: 'Share with outlet…' }, ...outlets.map((o) => ({ value: o.orgId, label: o.name }))]} />
                <PSelect size="sm" width={160} value={sel.product}
                  onChange={(v) => setPick((p) => ({ ...p, [conn.id]: { ...sel, product: v } }))}
                  options={products.map((p) => ({ value: p, label: productDisplayName(p) }))} />
                <PButton size="sm" disabled={!sel.outlet || !sel.product} onPress={share}>Share HQ number</PButton>
              </View>
            )}
          </View>
        );
      })}
    </View>
  );
};

const HqScreen: React.FC = () => {
  const { theme } = useTheme();
  const c = theme.colors;
  const { org, role } = useSession();
  const orgId = org?.id ?? '';
  const canEdit = role === 'org_admin';
  const [view, setView] = useState<HqOverview | null>(null);
  const [activity, setActivity] = useState<HqActivity[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [send, setSend] = useState<{ outlet: Outlet; kind: 'units' | 'money' } | null>(null);

  const load = useCallback(() => {
    if (!orgId) return;
    getHqOverview(orgId).then(
      (v) => { setView(v); setError(null); },
      (e) => setError(getApiErrorMessage(e, "Couldn't load outlet balances.")),
    );
    getHqActivity(orgId).then(setActivity, () => setActivity([]));
  }, [orgId]);
  useEffect(load, [load]);

  const items = useMemo(() => {
    const seen = new Map<string, string>();
    view?.outlets.forEach((o) => o.units.forEach((u) => { if (!seen.has(u.item)) seen.set(u.item, u.label); }));
    return Array.from(seen, ([item, label]) => ({ item, label }));
  }, [view]);

  return (
    <ScrollView contentContainerStyle={{ padding: 28, maxWidth: 1200, width: '100%', alignSelf: 'center' }}>
      <SettingsHeader title="HQ Console" sub="Every outlet's balances, in one place. Fund outlets and share HQ numbers." />
      {error && <View style={{ marginBottom: 16 }}><Alert tone="danger">{error}</Alert></View>}

      {!view && !error ? <ActivityIndicator color={c.accent} /> : !view ? null : view.outlets.length === 0 ? (
        <PEmpty icon="store" title="No outlets yet">Outlets linked to this organisation appear here with their balances.</PEmpty>
      ) : (
        <>
          <Panel title="Outlet balances" bodyStyle={{ padding: 0 }}>
            <DataTable<Outlet>
              minWidth={360 + items.length * 150}
              rows={view.outlets}
              keyExtractor={(o) => o.orgId}
              columns={[
                {
                  key: 'name', header: 'Outlet', width: 200, render: (o) => (
                    <View style={{ gap: 4 }}>
                      <Text style={{ color: c.ink, fontWeight: FW.medium }}>{o.name}</Text>
                      <Tag tone={o.relationship === 'franchise' ? 'info' : 'neutral'}>{o.relationship === 'franchise' ? 'Franchise' : 'Company-owned'}</Tag>
                    </View>
                  ),
                },
                ...items.map(({ item, label }) => ({
                  key: item, header: label, width: 150, align: 'right' as const, render: (o: Outlet) => {
                    const u = o.units.find((x) => x.item === item);
                    if (!u) return <Text style={{ color: c.ink3 }}>—</Text>;
                    return (
                      <View style={{ alignItems: 'flex-end', gap: 2 }}>
                        <Text style={{ fontFamily: theme.font.mono, color: c.ink }}>{u.display}</Text>
                        {u.low && <Tag tone="warn">Low</Tag>}
                      </View>
                    );
                  },
                })),
                {
                  key: 'wallet', header: 'Wallet', width: 150, align: 'right', render: (o) => (
                    <View style={{ alignItems: 'flex-end', gap: 2 }}>
                      <Text style={{ fontFamily: theme.font.mono, color: c.ink }}>{o.wallet.display}</Text>
                      {o.wallet.low && <Tag tone="warn">Low</Tag>}
                    </View>
                  ),
                },
                {
                  key: 'act', header: '', width: 210, align: 'right', render: (o) => canEdit ? (
                    <View style={{ flexDirection: 'row', gap: 6 }}>
                      <PButton size="sm" onPress={() => setSend({ outlet: o, kind: 'units' })}>Send credits</PButton>
                      <PButton size="sm" onPress={() => setSend({ outlet: o, kind: 'money' })}>Send money</PButton>
                    </View>
                  ) : null,
                },
              ]}
            />
          </Panel>

          <Panel title="Shared numbers" sub="Let outlets send from an HQ number, with a monthly cap per outlet.">
            <SharedNumbers orgId={orgId} outlets={view.outlets} canEdit={canEdit} />
          </Panel>
        </>
      )}

      <Panel title="Activity" sub="Transfers and sharing changes, newest first.">
        {activity.length === 0 ? <Text style={{ color: c.ink3 }}>Nothing yet.</Text> : (
          <View style={{ gap: 10 }}>
            {activity.map((a) => (
              <View key={a.id} style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                <Tag tone={a.kind === 'transfer' ? 'accent' : 'neutral'}>{a.kind === 'transfer' ? 'Transfer' : 'Sharing'}</Tag>
                <Text style={{ color: c.ink, flexShrink: 1 }}>{a.summary}</Text>
                <Text style={{ color: c.ink3, fontSize: FS.sm }}>{a.actorName} · {new Date(a.at).toLocaleString('en-GB')}</Text>
              </View>
            ))}
          </View>
        )}
      </Panel>

      {view && send && (
        <TransferModal outlet={send.outlet} kind={send.kind} hq={view.hq} onClose={() => setSend(null)} onDone={load} />
      )}
    </ScrollView>
  );
};

export default HqScreen;
